import { queryOptions } from '@tanstack/react-query'
import { SpendMetricsResponse } from 'src/models/spendMetrics.models'
import { SpendMetricsRequestParams } from 'src/queries/spendMetrics/spendMetrics.models'
import { getSpendMetrics } from 'src/queries/spendMetrics/spendMetrics.network'
import { toDollars } from 'src/utils/currency.utils'

export const spendMetricsQueryKeys = {
  all: () => ['spendMetrics'] as const,
  read: (params: SpendMetricsRequestParams) =>
    [...spendMetricsQueryKeys.all(), 'read', params] as const,
}

export const spendMetricsQueries = {
  read: (params: SpendMetricsRequestParams) =>
    queryOptions({
      queryKey: spendMetricsQueryKeys.read(params),
      queryFn: () => getSpendMetrics(params),
      // The old postSpendMetrics thunk was single-shot (no retry); preserve
      // that so a failed read reveals the metrics box immediately instead of
      // keeping it hidden through React Query's default 3 retries.
      retry: false,
    }),
}

/**
 * Graph points for AccountMetricsSummaryBox / SpendGraph. Ported verbatim from
 * the deleted `selectGraphData` (account/budget overview selectors): x = the
 * dataSet date, y = the first series value converted cents -> dollars. Returns
 * `[]` until both `dataSets` and `series` are present.
 */
export const spendMetricsToGraphData = (
  data: SpendMetricsResponse | undefined,
): Array<{ x: number; y: number }> =>
  data?.dataSets !== undefined && data?.series !== undefined
    ? data.dataSets.map((date, i) => ({
        x: date,
        y: toDollars(data.series[0].data[i]),
      }))
    : []

/**
 * Total spend in raw cents (AccountMetricsSummaryBox applies toDollars at
 * render). Ported verbatim from the deleted `selectTotalSpent`. Returns
 * `undefined` until the first series is present.
 */
export const spendMetricsTotalSpent = (
  data: SpendMetricsResponse | undefined,
): number | undefined =>
  data?.series !== undefined && data.series[0] !== undefined
    ? data.series[0].data.reduce((acc, curr) => acc + curr, 0)
    : undefined
